import classNames from 'classnames'
import { LitElement, html, css, nothing, type PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import _ from 'lodash'
import {
    fuzzyFilter,
    isTeam,
    isUser,
    type Assignable,
    type AssigneeIds,
} from '../../shared/domain/schedule/assignee.no-deps'
import type { TeamWithCount } from '../../shared/domain/teams/types.no-deps'
import type { UserProfile } from '../../shared/domain/users/types.no-deps'
import type { SupportedLanguage } from '../i18n/i18n.no-deps'
import { timeout } from '../lib/timeout.no-deps'
import './zaptic-assignable-icon'
import './zaptic-button'
import './zaptic-delete-icon'
import './zaptic-deleted-assignee'
import './zaptic-icon'
import './zaptic-popover'
import './zaptic-profile-circle'
import type { UserList } from './zaptic-user-list'

export enum Events {
    /**
     * @deprecated use  onSelectedIdsChanged
     */
    onChange = 'onChange',
    onSelectedIdsChanged = 'onSelectedIdsChanged',
}

enum View {
    All = 1,
    Users,
    Teams,
}

type AllTeamsAssignable = Assignable

export interface CustomEvents {
    /**
     * @deprecated use  onSelectedIdsChanged
     */
    [Events.onChange]: CustomEvent<Assignable[]>
    [Events.onSelectedIdsChanged]: CustomEvent<AssigneeIds>
}

@customElement('zaptic-assignee-select')
export class AssigneeSelect extends LitElement {
    static styles = css`
        :host {
            display: flex;
            width: 100%;
        }

        .root {
            display: flex;
            flex-direction: column;
            height: 100%;
            width: 100%;
        }

        .root > div {
            height: 100%;
        }

        .main {
            flex-grow: 1;
            border: none;
            font-weight: var(--font-weight-regular);
            font-size: 16px;
            font-family: inherit;
        }

        .main::placeholder {
            color: var(--grey-500);
        }

        .main:focus {
            outline: none;
        }

        .root--full-mode .main {
            margin-left: 8px;
        }

        .assignee-input-wrapper {
            display: flex;
            border: 1px solid #b2c3db;
            border-radius: 5px;
            align-items: center;
        }

        .assignee-input-wrapper:focus {
            border: 1px solid var(--purple);
        }

        .assignee-input-wrapper:focus-within {
            border: 1px solid var(--purple);
        }

        .root--full-mode .assignee-input-wrapper {
            margin: 15px;
        }

        .root--full-mode .assignee-input-wrapper.marginless-search-box {
            /* we only need the bottom margin to maintain separation from the user list */
            margin: 0 0 8px 0;
        }

        .assignee-input {
            display: flex;
            flex: 1;
            flex-wrap: wrap;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
        }

        .assignee-input--limited-height {
            max-height: 100px;
            overflow-y: auto;
        }

        .magnifier-icon {
            line-height: 0;
        }

        .selected {
            display: flex;
            align-items: center;
            border: 1px solid #e8f1f9;
            padding: 5px;
            font-weight: var(--font-weight-medium);
            font-size: 14px;
            color: var(--darkGrey);
            justify-content: space-between;
            background: #e8f1f9;
            border-radius: 20px;
        }

        .selected > zaptic-delete-icon {
            display: flex;
        }

        .deleted {
            border: 1px dashed var(--grey5);
        }

        .selected-assignee {
            display: flex;
            align-items: center;
            flex-grow: 1;
        }

        .selected-assignee > zaptic-assignable-icon {
            padding-right: 10px;
        }

        .selected-assignee > zaptic-profile-circle {
            --ProfileCircle-size: 16px;
        }

        .selected-assignee-name {
            display: flex;
            align-items: center;
            text-align: center;
            margin: 0px 5px;
            font-weight: var(--font-weight-medium);
            line-height: 14px;
            color: var(--darkGrey);
        }

        .selected-single-assignee {
            display: flex;
            width: 100%;
            align-items: center;
            margin: 8px 16px;
            color: var(--darkGrey);
            font-weight: var(--font-weight-medium);
            line-height: 19.12px;
        }

        .selected-single-assignee > zaptic-assignable-icon {
            padding-right: 10px;
        }

        .button-row {
            display: flex;
            margin: 10px 5px;
        }

        .button-row > button {
            flex: 0 1 auto;
            justify-content: center;
            align-items: center;
            margin: 0px 4px;
            padding: 10px 15px;
            cursor: pointer;
            font-weight: var(--font-weight-medium);
            background-color: var(--disabled);
            color: #4f5b68;
            border: none;
            border-radius: 20px;
            height: 32px;
            font-size: 14px;
            line-height: 14px;
        }

        .button-row > .active {
            background-color: var(--purple);
            color: white;
        }

        .footer {
            align-items: center;
            border-top: 1px solid var(--grey5);
            display: flex;
            height: 48px;
        }

        .footer--padded {
            padding: 0 1rem;
            justify-content: flex-end;
        }

        .select-all-teams-wrapper {
            justify-content: space-between;
        }

        .select-all-teams {
            display: flex;
            padding: 15px 0px 15px 15px;
        }

        .select-all-teams-text {
            display: flex;
            font-weight: var(--font-weight-regular);
            color: var(--darkGrey);
            line-height: 14px;
            align-items: center;
            text-align: center;
        }

        .select-all-teams-done-button {
            width: 60px;
            height: 32px;
            margin: 8px 17px;
            color: var(--white);
            background-color: var(--purple);
            border: 1px solid var(--purple);
            border-radius: 4px;
            cursor: pointer;
        }

        .chevron-up {
            transform: rotate(180deg);
        }

        .delete-all-icon {
            display: flex;
            margin-right: 15px;
        }

        .display-no-data {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 250px;
        }

        .display-no-data-text {
            padding-bottom: 15px;
            color: var(--grey3);
            font-weight: var(--font-weight-medium);
            font-size: 14px;
        }

        .help-text {
            display: flex;
            margin: 15px;
            color: var(--grey-600);
            font-family: var(--font-medium);
            font-size: var(--small);
            font-style: normal;
        }

        .chevron-icon {
            padding: 0.7rem 2rem 0.7rem 0;
            cursor: pointer;
        }
    `

    @property()
    placeholder: string

    @property({ type: Array })
    users: UserProfile[] = []

    @property({ type: Array })
    teams: TeamWithCount[] = []

    /**
     * @deprecated use  selectedIds
     */
    @property({ type: Array })
    selected: Assignable[] = []

    @property({ type: Object })
    selectedIds: AssigneeIds = { teams: [], users: [] }

    @property()
    language: SupportedLanguage

    @property({ type: Boolean })
    disabled = false

    @property({ type: Boolean })
    'multi-select' = false

    @property({ type: Boolean })
    displaySelectedAssigneeInSearchInput: boolean = true

    @property()
    'no-data-message': string | null = null

    @property()
    'help-text': string | null = null

    @property({ type: Boolean })
    'allow-select-all-teams': boolean = false

    // Ensures that the 'all-procedure-teams-selected' value persists
    // even after a page refresh.
    @property({ type: Boolean })
    'all-procedure-teams-selected': boolean = false

    @property({ type: Boolean })
    'search-dropdown' = true

    @property({ type: Boolean })
    'show-clear-all-button' = true

    @property({ type: Boolean, attribute: 'limit-height' })
    limitHeight = false

    @property({ type: Boolean, attribute: 'use-jobtitle-as-user-subtitle' })
    useJobtitleAsUserSubtitle = false

    @property({ type: String })
    size: 'small' | 'large' = 'large'

    // This is just a visual thing, sometimes we want the input at the top and the buttons below it
    @property({ type: Boolean })
    'input-search-at-top' = false

    /** By default the input area does not have margins.
     * But when `search-dropdown` is disabled (so the select list is always visible)
     * the input is given margings.  Set this to `true` to avoid those margins.
     */
    @property({ type: Boolean })
    marginlessSearchBox = false

    @state()
    _allSelectionInputsMerged = new Array<Assignable>()

    @state()
    _filter: string = ''

    @state()
    _focused: boolean = false

    @state()
    _selectedIndex: number = 0

    @state()
    _view: View = View.All

    @state()
    _filteredItems: Assignable[]

    @state()
    _allTeamsSelected: boolean = false

    @query('zaptic-user-list')
    _userList: UserList

    @query('.main')
    _inputMain: HTMLElement

    @query('.assignee-input-wrapper')
    _assigneeInputWrapper: HTMLElement

    @query('.root')
    _root: HTMLElement

    connectedCallback() {
        super.connectedCallback()
        this._allTeamsSelected = this['all-procedure-teams-selected'] || false
    }

    willUpdate(changedProperties: PropertyValues<AssigneeSelect>) {
        if (changedProperties.has('_filter') || changedProperties.has('_view')) {
            this._selectedIndex = 0
            this._filteredItems = this.getFilteredItems()
        }
        if (
            changedProperties.has('selectedIds') ||
            changedProperties.has('selected') ||
            changedProperties.has('users') ||
            changedProperties.has('teams')
        ) {
            const teams = this.teams.filter(({ id }) => this.selectedIds.teams.includes(id))
            const users = this.users.filter(({ userId }) => this.selectedIds.users.includes(userId))
            this._allSelectionInputsMerged = [...teams, ...users, ...this.selected]
        }
    }

    updated(changedProperties: PropertyValues<AssigneeSelect>) {
        if (changedProperties.has('_filteredItems')) {
            this.scrollSelectedIntoView()
        }
    }

    private scrollSelectedIntoView() {
        if (this._userList != null) {
            this._userList.scrollSelectedIntoView()
        }
    }

    private getFilteredItems() {
        const items =
            this._view === View.All
                ? [...this.users, ...this.teams]
                : this._view === View.Users
                  ? this.users
                  : this.teams
        const cleanItems = items.filter((item) => item.name !== '')
        return !this._filter.trim() ? cleanItems : fuzzyFilter(cleanItems, this._filter)
    }

    private assigneeSelectedAlready(profile: Assignable) {
        return _.some(this._allSelectionInputsMerged, profile)
    }

    private removeAssignee(assigneeToRemove: Assignable) {
        this.setAssignees(this._allSelectionInputsMerged.filter(({ name }) => name !== assigneeToRemove.name))
    }

    private addAssignee(profile: Assignable) {
        const shouldMembersOfAssigneeTeamBeRemoved = this['multi-select'] && isTeam(profile)
        // If a team is selected, remove all existing selected users for that team
        if (shouldMembersOfAssigneeTeamBeRemoved) {
            const membersOfAssigneeTeam = this._allSelectionInputsMerged
                .filter(isUser)
                .map((user) => user.userId)
                .filter((userId) => profile.members?.includes(userId))
            const assigneesWithoutNewTeamMembers = this._allSelectionInputsMerged.filter(
                (assignee) => isTeam(assignee) || (isUser(assignee) && !membersOfAssigneeTeam.includes(assignee.userId))
            )
            this.setAssignees(assigneesWithoutNewTeamMembers.concat(profile))
        } else {
            this.setAssignees(this._allSelectionInputsMerged.concat(profile))
        }
    }

    private userTeamIsAlreadySelected(profile: Assignable) {
        if (isUser(profile)) {
            const userTeamsIds = profile.teams?.map((team) => team.id)
            const selectedTeamIds = this._allSelectionInputsMerged.filter(isTeam).map((team) => team.id)
            return _.intersection(userTeamsIds, selectedTeamIds).length
        }
        return false
    }

    private focusHandler(value: boolean) {
        return {
            handleEvent: async (evt: FocusEvent) => {
                // Prevent bluring if we are going from one child to another
                if (this.renderRoot.contains(evt.relatedTarget as Node) && evt.type === 'blur') {
                    return
                }

                // Add wait to avoid race condition between 2 html renders (1 from here and one from handle select)
                await timeout(100)

                this._focused = value
            },
            capture: true,
        }
    }

    private handleHover({ detail }: { detail: number }) {
        this._selectedIndex = detail
    }

    private handleSelect({ detail }: { detail: number }) {
        const item: Assignable = this._filteredItems[detail]

        // If all teams option is selected and then a team is removed, toggle all team selection
        // else return as we don't want to select any user when all team option is selected
        if ((this._allTeamsSelected && isUser(item)) || this.userTeamIsAlreadySelected(item)) {
            return
        }

        if (this._allTeamsSelected) {
            this.dispatchEvent(new CustomEvent('toggle-all-procedure-teams'))
            this._allTeamsSelected = false
        }

        if (this.assigneeSelectedAlready(item)) {
            this.removeAssignee(item)
        } else {
            this.addAssignee(item)
        }

        // If in multi-select mode, keep dropdown list open
        this._focused = this['multi-select']

        // focus on search input after selection when in multi-select mode
        if (this['multi-select']) {
            this._inputMain.focus()
        }
    }

    private handleDeselect(setFilter = '', deselectedAssignee: Assignable) {
        // If a assignee is removed and all temas was true then call a function to set it to false
        if (this._allTeamsSelected) {
            this.dispatchEvent(new CustomEvent('toggle-all-procedure-teams'))
            this._allTeamsSelected = false
            this.setAssignees([])
        } else {
            // Delete de-selected assignee from selected assignee array
            this.removeAssignee(deselectedAssignee)
        }

        this._filter = setFilter

        // focus on search input
        this._inputMain.focus()
    }

    private handleFilter(evt: any) {
        this._filter = evt.target.value
    }

    private handleKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                this._selectedIndex = Math.min(this._selectedIndex + 1, this._filteredItems.length - 1)
                break
            case 'ArrowUp':
                event.preventDefault()
                this._selectedIndex = Math.max(this._selectedIndex - 1, 0)
                break
            case 'Tab':
            case 'Enter':
                event.preventDefault()
                this.handleSelect({ detail: this._selectedIndex })
                return
            default:
                return
        }

        this.scrollSelectedIntoView()
    }

    private assigneeLabel(selectedAssignee: Assignable) {
        // In multi select mode de-selecting assignee will preserve what was in search filter
        // In single select mode de-selecting assignee will add user name to filter as before
        return html`
            <div class="selected-assignee" tabindex="0">
                ${isUser(selectedAssignee)
                    ? html` <zaptic-profile-circle
                          name=${selectedAssignee.name}
                          color=${selectedAssignee.profileCircleColor}
                          profile-image-url=${selectedAssignee.profileImageUrl}
                      ></zaptic-profile-circle>`
                    : ''}
                <span class="selected-assignee-name">${selectedAssignee?.name}</span>
            </div>
        `
    }

    private displayRemovableAssigneeTag(
        selectedAssignee: Assignable | AllTeamsAssignable,
        options: { disableMultiselect: boolean }
    ) {
        const deleted = isUser(selectedAssignee) && selectedAssignee.isDeleted
        const label = deleted ? window.i18n('shared:assigneeSelect.deltedUser') : this.assigneeLabel(selectedAssignee)

        const classes = deleted ? 'selected deleted' : 'selected'

        return html`
            <div class="${classes}" style=${options.disableMultiselect ? 'cursor: default;' : 'cursor: pointer;'}>
                ${label} ${options.disableMultiselect ? '' : this.deleteIcon(selectedAssignee)}
            </div>
        `
    }

    private deleteIcon(selectedAssignee: Assignable) {
        return html`<zaptic-delete-icon
            @click=${() => this.handleDeselect('', selectedAssignee)}
            @keyup=${(evt: KeyboardEvent) => evt.key === 'Enter' && this.handleDeselect('', selectedAssignee)}
        ></zaptic-delete-icon>`
    }

    private searchInput() {
        const placeholder = this._allSelectionInputsMerged.length === 0 ? this.placeholder : ''
        const value = this._allSelectionInputsMerged.length === 0 || this._focused ? this._filter : ''

        return html`<input
            class="main"
            data-testid="assignee-select-input"
            @input=${this.handleFilter}
            .value=${value}
            placeholder=${placeholder}
            @keydown=${this.handleKeyDown}
            ?disabled=${this.disabled}
            style=${this.disabled ? 'visibility: hidden;' : ''}
        />`
    }

    private async unselectAllAssignees() {
        if (this._allTeamsSelected) {
            this.dispatchEvent(new CustomEvent('toggle-all-procedure-teams'))
            this._allTeamsSelected = false
        }
        this.setAssignees([])

        // Add some wait to avoid race condition between 2 renders
        await timeout(100)
        this._inputMain.focus()
    }

    private displayDeletedAssignee() {
        const deletedAssigneeLabel = window.i18n('shared:assigneeSelect.deltedUser')

        return html`
            <div class="selected-single-assignee">
                <zaptic-deleted-assignee
                    @onClick=${() => this.unselectAllAssignees()}
                    label=${deletedAssigneeLabel}
                ></zaptic-deleted-assignee>
            </div>
        `
    }

    private displaySingleAssignee(selectedAssignee: Assignable) {
        if (isUser(selectedAssignee) && selectedAssignee.isDeleted) {
            return this.displayDeletedAssignee()
        }

        return html`<div class="selected-single-assignee">
            <zaptic-assignable-icon .assignee=${selectedAssignee}></zaptic-assignable-icon>
            ${selectedAssignee.name}
        </div>`
    }

    // the pills with the users or team names in them
    private renderSelectedAssignees(options: { disableMultiselect: boolean }) {
        const renderMergedSelections =
            this._allSelectionInputsMerged?.length && this.displaySelectedAssigneeInSearchInput

        if (!renderMergedSelections) {
            return nothing
        }

        if (this['multi-select']) {
            if (this._allTeamsSelected) {
                return this.displayRemovableAssigneeTag(
                    {
                        name: window.i18n('shared:assigneeSelect.allProcedureTeams'),
                        id: 0,
                        count: 0,
                        integrationId: '',
                    },
                    {
                        disableMultiselect: options.disableMultiselect,
                    }
                )
            }

            return html`${this._allSelectionInputsMerged.map((selectedAssignee) => {
                return this.displayRemovableAssigneeTag(selectedAssignee, {
                    disableMultiselect: options.disableMultiselect,
                })
            })} `
        }

        return html`${this._allSelectionInputsMerged.map((selectedAssignee) => {
            return this.displaySingleAssignee(selectedAssignee)
        })} `
    }

    // this big box with the search input, selected assignees being users and/or teams
    private renderAssigneeInput(options: { noAssigneeSelectedInSingleMode: boolean; disableMultiselect: boolean }) {
        // the input box where people can search for users/teams
        const renderSearchInput = options.noAssigneeSelectedInSingleMode || this['multi-select']

        return html`<div class="assignee-input ${this.limitHeight ? 'assignee-input--limited-height' : ''}">
            ${!this['search-dropdown']
                ? html`<div class="magnifier-icon"><zaptic-icon icon-name="magnifier"></zaptic-icon></div>`
                : nothing}
            ${this.renderSelectedAssignees(options)} ${renderSearchInput ? this.searchInput() : ''}
        </div>`
    }

    private renderClearSearchDeleteIcon() {
        if (!this.displaySelectedAssigneeInSearchInput && this._filter.length) {
            return html`<zaptic-delete-icon
                @click=${this.clearSearchInputValue}
                class="delete-all-icon"
            ></zaptic-delete-icon>`
        }

        return nothing
    }

    private renderUnselectAllDeleteIcon(options: { disableMultiselect: boolean }) {
        const isDeletedUser =
            this._allSelectionInputsMerged.length === 1 &&
            isUser(this._allSelectionInputsMerged[0]) &&
            this._allSelectionInputsMerged[0].isDeleted

        const shouldDisplayDeleteIcon =
            this.displaySelectedAssigneeInSearchInput &&
            this._allSelectionInputsMerged.length &&
            !options.disableMultiselect &&
            !isDeletedUser

        if (!shouldDisplayDeleteIcon) {
            return nothing
        }

        return html`<zaptic-delete-icon
            @click=${this.unselectAllAssignees}
            class="delete-all-icon"
            data-testid="clear-all-assignee-select"
        ></zaptic-delete-icon>`
    }

    private chevronIcon(className: string) {
        return html`
            <svg
                class=${className}
                width="12"
                height="7"
                viewBox="0 0 12 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M11.7365 0.225291C12.0458 0.530861 12.0458 1.03024 11.7365 1.33581L6.90974 6.10455C6.39859 6.60955 5.56987 6.60955 5.05873 6.10455L0.231966 1.33581C-0.0773221 1.03024 -0.077322 0.530861 0.231966 0.225291C0.536009 -0.0750969 1.0251 -0.0750972 1.32914 0.225291L5.98423 4.82442L10.6393 0.225291C10.9434 -0.075097 11.4325 -0.0750967 11.7365 0.225291Z"
                    fill="#7E8B9E"
                />
            </svg>
        `
    }

    private renderChevronIcon(options: { shouldRenderChevronIcon: boolean }) {
        if (!options.shouldRenderChevronIcon) {
            return nothing
        }

        return this._focused
            ? html`<div
                  class="chevron-icon"
                  @click=${() => {
                      // Blur all elements when chevron-up icon is clicked
                      this._inputMain.blur()
                      this._assigneeInputWrapper.blur()
                      this._root.blur()
                  }}
              >
                  ${this.chevronIcon('chevron-up')}
              </div>`
            : html`<div class="chevron-icon">${this.chevronIcon('')}</div>`
    }

    private assigneeSearchInput() {
        // If either no assignee is selected in single select mode
        const noAssigneeSelectedInSingleMode = !this['multi-select'] && !this._allSelectionInputsMerged.length

        // Multiselect mode and is disabled
        const disableMultiselect = this['multi-select'] && this.disabled

        const shouldRenderChevronIcon = !disableMultiselect && this['search-dropdown']

        // Multiselect mode and is not disabled
        const shouldTakeMultiselectInput = this['multi-select'] && !this.disabled

        const shouldTakeInput = !this['multi-select']
            ? noAssigneeSelectedInSingleMode
            : !disableMultiselect || shouldTakeMultiselectInput

        return html`<div
            class=${classNames('assignee-input-wrapper', {
                'marginless-search-box': this.marginlessSearchBox,
            })}
            tabindex=${shouldTakeInput ? '-1' : ''}
        >
            ${this.renderAssigneeInput({ noAssigneeSelectedInSingleMode, disableMultiselect })}
            ${this.renderClearSearchDeleteIcon()} ${this.renderUnselectAllDeleteIcon({ disableMultiselect })}
            ${this.renderChevronIcon({ shouldRenderChevronIcon })}
        </div>`
    }

    private toggleAllTeamsSelection() {
        this._allTeamsSelected = !this._allTeamsSelected

        this.dispatchEvent(new CustomEvent('toggle-all-procedure-teams'))

        this.setAssignees(this._allTeamsSelected ? this.teams : [])

        // if no teams are selected after toggling all teams, bring focus to input
        if (!this._allTeamsSelected) {
            this._inputMain.focus()
        }
    }

    private handleDone() {
        this._focused = false
    }

    private selectAllTeamsOption() {
        return html`
            <div class="footer select-all-teams-wrapper">
                <div class="select-all-teams">
                    <zaptic-checkbox
                        .selected=${this._allTeamsSelected}
                        @onChange=${() => this.toggleAllTeamsSelection()}
                    ></zaptic-checkbox>
                    <span class="select-all-teams-text"> ${window.i18n('shared:assigneeSelect.selectAllTeams')} </span>
                </div>
                <button class="select-all-teams-done-button" @click=${this.handleDone}>
                    ${window.i18n('shared:common.done')}
                </button>
            </div>
        `
    }

    private assigneeFilterButtons = () => {
        if (this.users.length === 0 || this.teams.length === 0) {
            return nothing
        }

        // Mousedown here stops the click from bubbling up to the parent
        // and closing the dropdown
        return html`
            <span
                class="button-row"
                @mousedown=${(e: MouseEvent) => {
                    e.preventDefault()
                    e.stopPropagation()
                }}
            >
                <button @click=${() => (this._view = View.All)} class=${this._view === View.All ? 'active' : ''}>
                    ${window.i18n('shared:assigneeSelect.viewAll')}
                </button>
                <button @click=${() => (this._view = View.Users)} class=${this._view === View.Users ? 'active' : ''}>
                    ${window.i18n('shared:assigneeSelect.users')}
                </button>
                <button @click=${() => (this._view = View.Teams)} class=${this._view === View.Teams ? 'active' : ''}>
                    ${window.i18n('shared:assigneeSelect.teams')}
                </button>
            </span>
        `
    }

    private clearSearchInputValue() {
        this._filter = ''
    }

    private clearAllButtonRow() {
        return html`
            <div class="footer footer--padded">
                <zaptic-button
                    size="small"
                    type="secondary"
                    @click="${this.unselectAllAssignees}"
                    disabled="${!this._allSelectionInputsMerged.length}"
                    >${window.i18n('shared:common.clearAll')}</zaptic-button
                >
            </div>
        `
    }

    private assigneeOptions() {
        return html`
            <zaptic-user-list
                .items=${this._filteredItems}
                .selected=${this._selectedIndex}
                .language=${this.language}
                .multi-select-options=${this['multi-select'] ? { allTeamsSelected: this._allTeamsSelected } : null}
                .selected-items=${this._allSelectionInputsMerged}
                .use-jobtitle-as-user-subtitle=${this.useJobtitleAsUserSubtitle}
                .item-size=${this.size}
                .disabled=${this._allTeamsSelected}
                data-testid="user-list"
                @onHovered=${this.handleHover}
                @onSelected=${this.handleSelect}
            ></zaptic-user-list>
            ${this['multi-select'] && this['allow-select-all-teams'] ? this.selectAllTeamsOption() : ''}
            ${!this['search-dropdown'] && this['show-clear-all-button'] ? this.clearAllButtonRow() : ''}
        `
    }

    private setAssignees(updatedAssignees: Assignable[]) {
        this.dispatchEvent(new CustomEvent(Events.onChange, { detail: updatedAssignees }))
        this.dispatchEvent(
            new CustomEvent<AssigneeIds>(Events.onSelectedIdsChanged, {
                detail: {
                    teams: updatedAssignees.filter(isTeam).map(({ id }) => id),
                    users: updatedAssignees.filter(isUser).map(({ userId }) => userId),
                },
            })
        )
    }

    private hasAssigneeToDisplay() {
        return this.users.length + this.teams.length > 0
    }

    private displayNoDataMessage() {
        return html` <zaptic-popover style=${this._focused ? '' : 'display: none;'}>
            ${html`<div class="display-no-data">
                <div class="display-no-data-text">${this['no-data-message']}</div>
            </div>`}
        </zaptic-popover>`
    }

    /**
     * Renders the component with a search input that displays the rest of the component elements in a drop-down area.
     */
    private renderDropdownLayout() {
        const dropdownContent = () => html`
            <zaptic-popover style=${this._focused ? '' : 'display: none;'}>
                ${this.assigneeFilterButtons()} ${this.assigneeOptions()}
            </zaptic-popover>
        `

        return html`
            <div class="root" @focus=${this.focusHandler(true)} @blur=${this.focusHandler(false)}>
                ${this.assigneeSearchInput()}
                ${this.hasAssigneeToDisplay() ? dropdownContent() : this.displayNoDataMessage()}
            </div>
        `
    }

    /**
     * Renders the component with all of its elements fully visible.
     */
    private renderFullLayout() {
        const renderHelpText = this['help-text'] ? html`<span class="help-text">${this['help-text']}</span>` : nothing

        if (this['input-search-at-top']) {
            return html`<div class="root root--full-mode">
                <div>
                    ${this.assigneeSearchInput()} ${renderHelpText} ${this.assigneeFilterButtons()}
                    ${this.assigneeOptions()}
                </div>
            </div>`
        }

        return html`<div class="root root--full-mode">
            <div>${this.assigneeFilterButtons()} ${this.assigneeSearchInput()} ${this.assigneeOptions()}</div>
        </div>`
    }

    render() {
        // For some reason selected value becomes null after last items is deleted from it
        // This is set to empty array in case this happens
        if (this._allSelectionInputsMerged == null) {
            this._allSelectionInputsMerged = []
        }

        if (this['search-dropdown']) {
            return this.renderDropdownLayout()
        }

        return this.renderFullLayout()
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'zaptic-assignee-select': AssigneeSelect
    }
}
